import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Alert from "../../../Common/Alert";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import {
  ChartOfAccountStyles,
  formatDate,
  money,
  num,
} from "../../Accounts/Chart-Of-Account/ChartOfAccountShared";
import {
  FinancialStatementStyles,
  PresetChips,
  endOfLastMonth,
  endOfLastYear,
  printStatement,
  today,
} from "./FinancialStatementShared";
import {
  SbStyles,
  csvCell,
  daysBetween,
  escapeHtml,
} from "./SupplierBalanceReport";

const STATUSES = [
  ["Open", "open"],
  ["Completed", "completed"],
  ["All", "all"],
];

const STATUS_LABELS = {
  unpaid: "Unpaid",
  partial: "Partly paid",
  completed: "Completed",
};

const SORTS = [
  ["date", "Oldest first"],
  ["left", "Most left"],
  ["reference", "Reference"],
];

const side = (value) => (value === "dr" ? "Dr" : "Cr");

const paidShare = (row) =>
  num(row.amount) > 0 ? Math.min(100, (num(row.paid) * 100) / num(row.amount)) : 0;

const RefStyles = () => (
  <style>
    {`
      .rf-status {
        display: inline-block;
        padding: 1px 8px;
        border-radius: 8px;
        font-size: 0.66rem;
        font-weight: 600;
        white-space: nowrap;
      }
      .rf-status.is-unpaid { background: #fdecec; color: #e42320; }
      .rf-status.is-partial { background: #fdf3e2; color: #c77700; }
      .rf-status.is-completed { background: #e8f6ee; color: #1f9d55; }
      .rf-progress { display: flex; align-items: center; gap: 8px; min-width: 150px; }
      .rf-progress .sb-share-fill.is-done { background: #1f9d55; }
      .rf-ref { font-family: "SFMono-Regular", Menlo, Consolas, monospace; }
      .sb-ledger tr.rf-opened td { background: #f7f8fc; font-weight: 600; }
    `}
  </style>
);

// A reference's entries day by day: the voucher that opened it, then every
// voucher posted against it, with what was still left after each.
const ReferenceEntries = ({ row }) => (
  <table className="sb-ledger">
    <thead>
      <tr>
        <th>Date</th>
        <th>Journal Voucher</th>
        <th>Entry</th>
        <th>Remarks</th>
        <th className="coa-num">Debit</th>
        <th className="coa-num">Credit</th>
        <th className="coa-num">Paid</th>
        <th className="coa-num">Left</th>
      </tr>
    </thead>
    <tbody>
      {row.entries.map((entry) => (
        <tr
          key={entry.id}
          className={entry.kind === "opened" ? "rf-opened" : undefined}
        >
          <td style={{ whiteSpace: "nowrap" }}>{formatDate(entry.date)}</td>
          <td style={{ whiteSpace: "nowrap" }}>
            <Link to={`/journal-voucher-details/${entry.voucher_id}`}>
              {entry.voucher_no}
            </Link>
            <span className="sb-sub" style={{ textTransform: "capitalize" }}>
              {entry.voucher_type}
            </span>
          </td>
          <td>{entry.kind === "opened" ? "Reference opened" : "Posted against"}</td>
          <td>{entry.remarks}</td>
          <td className="coa-num">{num(entry.debit) ? money(entry.debit) : ""}</td>
          <td className="coa-num">{num(entry.credit) ? money(entry.credit) : ""}</td>
          <td className="coa-num">
            {entry.paid !== undefined ? money(entry.paid) : ""}
          </td>
          <td className="coa-num" style={{ fontWeight: 600 }}>
            {money(entry.left)}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const ReferenceReport = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [asOf, setAsOf] = useState(searchParams.get("as_of") || today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [account, setAccount] = useState("");
  const [status, setStatus] = useState("open");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("date");
  const [open, setOpen] = useState({});

  const fetchData = async (date = asOf) => {
    setLoading(true);
    setAlert(null);
    setOpen({});
    setSearchParams({ as_of: date }, { replace: true });

    try {
      const res = await Http.get("/reference-report", {
        params: { as_of: date },
      });
      setData(res.data);
    } catch (error) {
      console.error("Fetch error:", error);
      setData(null);
      setAlert({
        message:
          error.response?.data?.message ||
          "Could not load the report. Please try again.",
        type: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const applyDate = (date) => {
    setAsOf(date);
    fetchData(date);
  };

  // Account, status and search narrow the rows already loaded; only the date
  // needs a new request.
  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();
    const list = (data ? data.rows : []).filter(
      (row) =>
        (!account || String(row.chart_of_account_id) === account) &&
        (status === "all" ||
          (status === "completed"
            ? row.status === "completed"
            : row.status !== "completed")) &&
        (!query ||
          `${row.reference} ${row.account_code} ${row.account_name} ${row.voucher_no}`
            .toLowerCase()
            .includes(query)),
    );
    const compare = {
      date: (a, b) =>
        a.date.localeCompare(b.date) || a.reference.localeCompare(b.reference),
      left: (a, b) => num(b.left) - num(a.left),
      reference: (a, b) => a.reference.localeCompare(b.reference),
    }[sort];
    return list.sort(compare);
  }, [data, account, status, search, sort]);

  const totals = useMemo(
    () => ({
      amount: rows.reduce((sum, row) => sum + num(row.amount), 0),
      paid: rows.reduce((sum, row) => sum + num(row.paid), 0),
      left: rows.reduce((sum, row) => sum + num(row.left), 0),
    }),
    [rows],
  );

  const all = data ? data.rows : [];
  const openRows = all.filter((row) => row.status !== "completed");
  const openLeft = openRows.reduce((sum, row) => sum + num(row.left), 0);
  const completedCount = all.length - openRows.length;
  const staleCount = data
    ? openRows.filter((row) => daysBetween(row.date, data.as_of) > 90).length
    : 0;

  const selectedAccount = data
    ? data.accounts.find((a) => String(a.id) === account)
    : null;
  const scopeLabel = selectedAccount
    ? `${selectedAccount.code} - ${selectedAccount.name}`
    : "all accounts";
  const statusLabel = STATUSES.find(([, value]) => value === status)[0];

  const exportCsv = () => {
    const lines = [
      ["Reference Report"],
      [`As at ${data.as_of} · ${statusLabel} references · ${scopeLabel}`],
      [],
      [
        "Sl",
        "Reference",
        "Account Code",
        "Account",
        "Opened",
        "Journal Voucher",
        "Side",
        "Amount",
        "Paid",
        "Left",
        "Status",
        "Last Paid",
        "Days Open",
      ],
      ...rows.map((row, i) => [
        i + 1,
        row.reference,
        row.account_code,
        row.account_name,
        row.date,
        row.voucher_no,
        side(row.side),
        num(row.amount).toFixed(2),
        num(row.paid).toFixed(2),
        num(row.left).toFixed(2),
        STATUS_LABELS[row.status],
        row.last_paid || "",
        row.status === "completed" ? "" : daysBetween(row.date, data.as_of),
      ]),
      [],
      [
        "",
        "Total",
        "",
        "",
        "",
        "",
        "",
        totals.amount.toFixed(2),
        totals.paid.toFixed(2),
        totals.left.toFixed(2),
      ],
      [],
      ["Day-wise entries"],
      ["Reference", "Account Code", "Date", "Journal Voucher", "Entry", "Debit", "Credit", "Paid", "Left"],
      ...rows.flatMap((row) =>
        row.entries.map((entry) => [
          row.reference,
          row.account_code,
          entry.date,
          entry.voucher_no,
          entry.kind === "opened" ? "Reference opened" : "Posted against",
          num(entry.debit).toFixed(2),
          num(entry.credit).toFixed(2),
          entry.paid !== undefined ? num(entry.paid).toFixed(2) : "",
          num(entry.left).toFixed(2),
        ]),
      ),
    ];
    const csv = lines.map((line) => line.map(csvCell).join(",")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `reference-report_${data.as_of}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Every reference with its day-wise entries printed beneath it.
  const handlePrint = () => {
    const headHtml = `
      <th>Date</th><th>Journal Voucher</th><th>Entry</th>
      <th class="num">Debit</th><th class="num">Credit</th>
      <th class="num">Paid</th><th class="num">Left</th>`;
    const bodyHtml =
      rows
        .map(
          (row) => `
        <tr class="grand">
          <td colspan="3">${escapeHtml(row.reference)} &middot; ${escapeHtml(
            `${row.account_code} - ${row.account_name}`,
          )} &middot; ${STATUS_LABELS[row.status]}</td>
          <td class="num" colspan="2">${money(row.amount)} ${side(row.side)}</td>
          <td class="num">${money(row.paid)}</td>
          <td class="num">${money(row.left)}</td>
        </tr>
        ${row.entries
          .map(
            (entry) => `
        <tr>
          <td>${formatDate(entry.date)}</td>
          <td class="code">${escapeHtml(entry.voucher_no)}</td>
          <td>${entry.kind === "opened" ? "Reference opened" : "Posted against"}</td>
          <td class="num">${num(entry.debit) ? money(entry.debit) : ""}</td>
          <td class="num">${num(entry.credit) ? money(entry.credit) : ""}</td>
          <td class="num">${entry.paid !== undefined ? money(entry.paid) : ""}</td>
          <td class="num">${money(entry.left)}</td>
        </tr>`,
          )
          .join("")}`,
        )
        .join("") +
      `<tr class="grand">
        <td colspan="3">Total (${rows.length} reference${rows.length === 1 ? "" : "s"})</td>
        <td class="num" colspan="2">${money(totals.amount)}</td>
        <td class="num">${money(totals.paid)}</td>
        <td class="num">${money(totals.left)}</td>
      </tr>`;

    printStatement({
      title: "Reference Report",
      subtitle: `As at ${formatDate(data.as_of)} &middot; ${statusLabel} references &middot; Amounts in Taka`,
      headHtml,
      bodyHtml,
      note: `Journal voucher references on ${escapeHtml(
        scopeLabel,
      )}, counting every voucher dated up to the statement date.`,
    });
  };

  const stat = (label, icon, value, sub, className = "") => (
    <div className="col-md-3 mg-b-10">
      <div className={`coa-stat ${className}`}>
        <i className={`fa ${icon} coa-stat-icon`}></i>
        <span className="coa-stat-label">{label}</span>
        <span className="coa-stat-value">{value}</span>
        <span className="coa-stat-sub">{sub}</span>
      </div>
    </div>
  );

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <FinancialStatementStyles />
        <SbStyles />
        <RefStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">Reference Report</h5>
            <p className="text-muted mb-0 tx-12">
              Journal voucher references by chart of account, what is paid and
              what is left, as at {formatDate(data?.as_of || asOf)}
            </p>
          </div>
          <div className="d-flex gap-2 coa-no-print">
            <Link to="/reports" className="btn btn-outline-secondary">
              <i className="fa fa-arrow-left mg-r-6"></i>Reports
            </Link>
            <button
              className="btn btn-outline-secondary"
              onClick={() => fetchData()}
              title="Reload data"
              disabled={loading}
            >
              <i className="fa fa-refresh"></i>
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={exportCsv}
              disabled={loading || !rows.length}
            >
              <i className="fa fa-file-excel-o mg-r-6"></i>Export CSV
            </button>
            <button
              className="btn btn-primary"
              onClick={handlePrint}
              disabled={loading || !rows.length}
            >
              <i className="fa fa-print mg-r-6"></i>Print
            </button>
          </div>
        </div>

        <div className="am-pagebody">
          <Alert alert={alert} />

          <div className="coa-panel coa-no-print">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-filter me-2"></i>Filters
              </span>
              <span className="coa-head-note">
                Every journal voucher dated up to the statement date
              </span>
            </div>
            <div className="coa-panel-body">
              <div className="row align-items-start">
                <div className="col-lg-3">
                  <label>As at</label>
                  <input
                    type="date"
                    className="form-control"
                    value={asOf}
                    onChange={(e) => setAsOf(e.target.value)}
                  />
                  <PresetChips
                    presets={[
                      ["Today", today()],
                      ["Last month end", endOfLastMonth()],
                      ["Last year end", endOfLastYear()],
                    ]}
                    isActive={([, value]) => asOf === value}
                    onPick={([, value]) => applyDate(value)}
                  />
                </div>
                <div className="col-lg-2 mg-t-10 mg-lg-t-0">
                  <label>&nbsp;</label>
                  <button
                    className="btn btn-primary d-block"
                    onClick={() => fetchData()}
                    disabled={loading || !asOf}
                  >
                    <i
                      className={`fa ${loading ? "fa-spinner fa-spin" : "fa-search"} mg-r-6`}
                    ></i>
                    {loading ? "Loading..." : "Apply"}
                  </button>
                </div>
                <div className="col-lg-4 mg-t-10 mg-lg-t-0">
                  <label>Chart of Account</label>
                  <select
                    className="form-control"
                    value={account}
                    onChange={(e) => setAccount(e.target.value)}
                  >
                    <option value="">All accounts</option>
                    {(data?.accounts || []).map((a) => (
                      <option key={a.id} value={String(a.id)}>
                        {a.code} - {a.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-lg-3 mg-t-10 mg-lg-t-0">
                  <label>Status</label>
                  <PresetChips
                    presets={STATUSES}
                    isActive={([, value]) => status === value}
                    onPick={([, value]) => setStatus(value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {data && (
            <div className="row mg-b-20">
              {stat(
                "Left To Pay",
                "fa-hourglass-half",
                money(openLeft),
                `${openRows.length} open reference${openRows.length === 1 ? "" : "s"}`,
              )}
              {stat(
                "Paid So Far",
                "fa-check",
                money(data.totals.paid),
                `Of ${money(data.totals.amount)} referenced`,
              )}
              {stat(
                "Completed",
                "fa-check-circle-o",
                completedCount,
                `${all.length} reference${all.length === 1 ? "" : "s"} in total`,
                "is-ok",
              )}
              {stat(
                "Open Over 90 Days",
                "fa-clock-o",
                staleCount,
                staleCount
                  ? "Opened more than 90 days ago"
                  : "No open reference is older than 90 days",
                staleCount ? "is-alert" : "is-ok",
              )}
            </div>
          )}

          <div className="coa-panel">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-table me-2"></i>References
                <span className="coa-head-note mg-l-10">
                  Click a reference to see its entries day by day
                </span>
              </span>
              {data && data.rows.length > 0 && (
                <span className="sb-tools coa-no-print">
                  <select
                    className="form-control"
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    aria-label="Sort by"
                  >
                    {SORTS.map(([value, label]) => (
                      <option key={value} value={value}>
                        Sort: {label}
                      </option>
                    ))}
                  </select>
                  <div className="sb-search">
                    <i className="fa fa-search" aria-hidden="true"></i>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search reference, account or voucher"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      aria-label="Search references"
                    />
                  </div>
                </span>
              )}
            </div>

            {loading && !data ? (
              <div className="pd-y-10">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="fs-skeleton"></div>
                ))}
              </div>
            ) : !data ? (
              <div className="coa-empty">
                <i className="fa fa-exclamation-circle"></i>
                The report could not be loaded.
              </div>
            ) : !rows.length ? (
              <div className="coa-empty">
                <i className="fa fa-check-circle-o"></i>
                {data.rows.length
                  ? "No references match the filters."
                  : `No journal voucher references as at ${formatDate(data.as_of)}.`}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="sb-table">
                  <thead>
                    <tr>
                      <th>Sl</th>
                      <th>Reference</th>
                      <th>Chart of Account</th>
                      <th>Opened</th>
                      <th className="coa-num">Amount</th>
                      <th className="coa-num">Paid</th>
                      <th className="coa-num">Left</th>
                      <th>Progress</th>
                      <th>Last Paid</th>
                    </tr>
                  </thead>
                  <tbody style={{ opacity: loading ? 0.5 : 1 }}>
                    {rows.map((row, i) => {
                      const isOpen = !!open[row.key];
                      const done = row.status === "completed";
                      const days = daysBetween(row.date, data.as_of);
                      return (
                        <React.Fragment key={row.key}>
                          <tr className={`sb-row${isOpen ? " is-open" : ""}`}>
                            <td className="sb-sl">{i + 1}</td>
                            <td>
                              <button
                                type="button"
                                className="sb-name-btn rf-ref"
                                onClick={() =>
                                  setOpen((values) => ({
                                    ...values,
                                    [row.key]: !isOpen,
                                  }))
                                }
                                aria-expanded={isOpen}
                              >
                                <i
                                  className={`fa fa-caret-${isOpen ? "down" : "right"} mg-r-6`}
                                  aria-hidden="true"
                                ></i>
                                {row.reference}
                              </button>
                              <span className="sb-sub">
                                <span className={`rf-status is-${row.status}`}>
                                  {STATUS_LABELS[row.status]}
                                </span>{" "}
                                {row.postings} posting{row.postings === 1 ? "" : "s"}
                              </span>
                            </td>
                            <td>
                              <span className="coa-code">{row.account_code}</span>{" "}
                              {row.account_name}
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              {formatDate(row.date)}
                              <span className="sb-sub">
                                <Link to={`/journal-voucher-details/${row.voucher_id}`}>
                                  {row.voucher_no}
                                </Link>
                                {!done && ` · ${days} day${days === 1 ? "" : "s"} open`}
                              </span>
                            </td>
                            <td className="coa-num">
                              {money(row.amount)}
                              <span className="sb-sub">{side(row.side)}</span>
                            </td>
                            <td className="coa-num">{money(row.paid)}</td>
                            <td
                              className={`coa-num ${done ? "sb-settled" : ""}`}
                              style={{ fontWeight: 700, color: done ? undefined : "#2a2962" }}
                            >
                              {money(row.left)}
                            </td>
                            <td>
                              <div className="rf-progress">
                                <div className="sb-share-bar">
                                  <div
                                    className={`sb-share-fill${done ? " is-done" : ""}`}
                                    style={{ width: `${paidShare(row)}%` }}
                                  ></div>
                                </div>
                                <span className="sb-share-pct">
                                  {paidShare(row).toFixed(0)}%
                                </span>
                              </div>
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              {row.last_paid ? formatDate(row.last_paid) : "–"}
                            </td>
                          </tr>
                          {isOpen && (
                            <tr>
                              <td colSpan={9} className="sb-ledger-cell">
                                <ReferenceEntries row={row} />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={4} style={{ textAlign: "right" }}>
                        Total
                      </td>
                      <td className="coa-num">{money(totals.amount)}</td>
                      <td className="coa-num">{money(totals.paid)}</td>
                      <td className="coa-num">{money(totals.left)}</td>
                      <td colSpan={2}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferenceReport;
